import {endOfMonth, format, startOfMonth} from 'date-fns';
import {REPORT_TYPE} from '../../../constants';
import {ReportState} from '../../../forecast-app/reports-tab/reports-page/ReportTypes';
import {Condition, FieldNode, ReportDefinitionV1Tasks} from '../contracts/report-definition.types';
import {periodFromConditions} from '../periodFilter';

/** Saved-report filter row as the `saveReportAs` / `updateSavedReport` mutations accept it
 * (`GlobalSearchFilterType`). Same data as a report-definition condition, except the value list is
 * singular (`value`, not `values`). */
export interface SavedReportFilterInput {
	field: string;
	operator: Condition['operator'];
	value: string[];
	context: Condition['context'];
}

/** One visible data column on the wire, in display order. Width is omitted rather than null when the
 * definition does not set one, so the mutation input stays minimal. */
export interface SavedReportLayoutColumnInput {
	field: string;
	width?: number;
}

/** The column a saved report is sorted by. */
export interface SavedReportSortInput {
	field: string;
	direction: 'ASC' | 'DESC';
}

/** The four grouping slots a saved report carries, in place of the definition's grouping array. */
export interface SavedReportGroupingInput {
	groupingOne: string | null;
	groupingTwo: string | null;
	groupingThree: string | null;
	groupingFour: string | null;
}

/** What a save returns and a re-save needs back: the report's Relay id plus the fields an update
 * overwrites unconditionally and therefore has to echo (see `buildUpdateSavedReportInput`). */
/** A report that exists in storage.
 *
 * `id` is the RELAY GLOBAL ID — `base64("SavedReportType:<uuid>")` — as the save mutations return it,
 * not report-service's raw uuid. Everything downstream is built on that and none of it is a REST call
 * to report-service, however much the definition path looks like one:
 *
 * - the lifecycle mutations and `updateSavedReport` resolve their id with a bare `fromGlobalId`, which
 *   yields an empty id for anything else, so they fail silently on a raw uuid;
 * - `/report/definition/:reportId` is served by the graphql server, not report-service. It decodes via
 *   `toRawReportId` (`server.js`), which accepts either shape, so it is the one consumer that cannot
 *   tell you which id space is correct;
 * - `/report-builder/:reportId` carries whatever this holds, and the re-open path seeds this field
 *   straight back from that URL segment.
 *
 * Do not "correct" this to the raw uuid. Encoding at the mutation boundaries then double-encodes on the
 * re-open path, because the seed is already a global id — publish and discard then resolve to a report
 * that does not exist and fail with no error at all. */
export interface SavedReportRef {
	id: string;
	privateAccess: boolean;
}

/** `SaveReportAsInput` for creating a first-class saved report from a report definition. Declared
 * structurally rather than imported from the Relay artifact so this module and its tests compile
 * without a relay-compiler run. */
export interface SaveReportAsInputFields extends SavedReportGroupingInput {
	name: string;
	type: string;
	eyeApplied: string;
	privateAccess: boolean;
	filters: SavedReportFilterInput[];
	layout: SavedReportLayoutColumnInput[] | null;
	sort: SavedReportSortInput | null;
	startDate: string;
	endDate: string;
	datePreset: string | null;
	dateScope: string | null;
	source: 'AI';
	promptText: string | null;
	manuallyEdited: boolean;
}

/** `UpdateSavedReportInput` for re-saving an already-saved report. */
export interface UpdateSavedReportInputFields extends SavedReportGroupingInput {
	id: string;
	name: string;
	eyeApplied: string;
	reportService: true;
	state: ReportState;
	privateAccess: boolean;
	filters: SavedReportFilterInput[];
	layout: SavedReportLayoutColumnInput[] | null;
	sort: SavedReportSortInput | null;
	startDate: string;
	endDate: string;
	datePreset: string | null;
	dateScope: string | null;
	manuallyEdited: boolean;
	lockToken?: string;
}

/** New reports are private to their creator; granting other people access is a separate share
 * action, not part of saving. */
const DEFAULT_PRIVATE_ACCESS = true;

/** Collects every field path in the tree as dot-notation, deepest-first: a node's children are
 * emitted before the node itself.
 *
 * The order matters. The saved-report wire format for columns is the eye-options object, and the
 * graphql server rebuilds the definition's field tree from that object's key order. Emitting a
 * parent before its children would make the rebuilt tree list the parent's own path first and
 * reorder the columns on re-open, so this mirrors the server's own traversal (see
 * `EyeOptionsMapper` / `FieldBuilder` in the graphql service) to keep column order stable across a
 * save/re-open round trip. */
function collectFieldPaths(fields: FieldNode[], prefix: string, paths: string[]): void {
	for (const field of fields) {
		const path = prefix ? `${prefix}.${field.name}` : field.name;
		if (field.children && field.children.length > 0) {
			collectFieldPaths(field.children, path, paths);
		}
		paths.push(path);
	}
}

/** Serialises the definition's field tree into the eye-options JSON string the saved-report
 * mutations take for columns (`{"task.name": true, "task": true, ...}`). */
export function toEyeApplied(fields: FieldNode[]): string {
	const paths: string[] = [];
	collectFieldPaths(fields, '', paths);
	const eyeOptions: Record<string, boolean> = {};
	for (const path of paths) {
		eyeOptions[path] = true;
	}
	return JSON.stringify(eyeOptions);
}

/** Maps the definition's filter conditions onto the saved-report filter rows. Conditions are
 * forwarded verbatim apart from `values` → `value`; nothing is dropped, so a re-opened report
 * filters exactly as the one that was saved. */
export function toSavedReportFilters(definition: ReportDefinitionV1Tasks): SavedReportFilterInput[] {
	return definition.filter.filterConditions.map(condition => ({
		field: condition.field,
		operator: condition.operator,
		value: condition.values,
		context: condition.context,
	}));
}

/** Spreads the definition's ordered grouping tokens across the four saved-report grouping slots.
 * Unused slots are explicitly null so a re-save clears a grouping level that was removed. */
export function toSavedReportGroupings(definition: ReportDefinitionV1Tasks): SavedReportGroupingInput {
	const grouping = (definition.grouping ?? []) as string[];
	return {
		groupingOne: grouping[0] ?? null,
		groupingTwo: grouping[1] ?? null,
		groupingThree: grouping[2] ?? null,
		groupingFour: grouping[3] ?? null,
	};
}

/** Maps the definition's column layout onto the saved-report layout rows: same field key and width,
 * with the list order carrying each column's left-to-right position. Null when the definition stores no
 * layout, which clears any layout the report had.
 *
 * Layout persists column ORDER and WIDTH only. It is not a visibility list: `serializeDefinition` omits
 * a column the session has hidden, but a re-opened report derives its column set from the field tree
 * (see `orderedDataColumnIds`), so a hidden column reappears. Hiding is session state; persisting it
 * needs a visibility concept in the definition contract, which does not exist. */
export function toSavedReportLayout(definition: ReportDefinitionV1Tasks): SavedReportLayoutColumnInput[] | null {
	if (!definition.layout) {
		return null;
	}
	return definition.layout.map(column =>
		column.width !== undefined ? {field: column.field, width: column.width} : {field: column.field}
	);
}

/** The definition's sort column, or null when the report applies no sort (which clears a stored one). */
export function toSavedReportSort(definition: ReportDefinitionV1Tasks): SavedReportSortInput | null {
	return definition.sort ? {field: definition.sort.field, direction: definition.sort.direction} : null;
}

/** The report's period, always populated.
 *
 * A saved report MUST carry both dates. Consumers of a saved report format them without null-guarding —
 * the Standard task report page turns a null date into the literal string `"Invalid date"` and sends it
 * to the project search, which parses it as a number and fails the whole page with a 500. Every report
 * created the Standard way has dates because the create action always seeds the current month, so an
 * AI-generated definition (which expresses its period as filter conditions and often carries no
 * top-level dates) has to seed the same default rather than save nulls.
 *
 * The window is recovered from the definition's own date conditions before falling back to the
 * current month, because seeding the current month over a definition that HAS a range persists a
 * date pair contradicting the conditions the report actually filters on. That lie is not inert: the
 * builder reads the stored pair back in preference to the conditions, so a Q4 deadline report
 * reopened in August reports an August window, and changing its scope rewrites the range to August
 * — silently moving the report's period. The current month is still the last resort, for a
 * definition that genuinely has no range at all. */
function datesOf(definition: ReportDefinitionV1Tasks): {startDate: string; endDate: string} {
	const today = new Date();
	const fromConditions = periodFromConditions(definition.filter.filterConditions);
	return {
		startDate: definition.startDate ?? fromConditions.startDate ?? format(startOfMonth(today), 'yyyy-MM-dd'),
		endDate: definition.endDate ?? fromConditions.endDate ?? format(endOfMonth(today), 'yyyy-MM-dd'),
	};
}

/** Builds the create input for the first save of a report definition. `type` falls back to the
 * task-report type because v1 of the definition grammar only produces task reports and the field
 * is optional there, while the mutation requires it. `promptText` is the request the report was
 * generated from; pass null when there is none, so a report saved without one records none. */
export function buildSaveReportAsInput(
	definition: ReportDefinitionV1Tasks,
	promptText: string | null,
	manuallyEdited: boolean
): SaveReportAsInputFields {
	return {
		name: definition.name,
		type: definition.reportType ?? REPORT_TYPE.TASK_REPORT,
		eyeApplied: toEyeApplied(definition.fields),
		privateAccess: DEFAULT_PRIVATE_ACCESS,
		filters: toSavedReportFilters(definition),
		layout: toSavedReportLayout(definition),
		sort: toSavedReportSort(definition),
		// Records that the builder authored this report, so opening it from the Reports list comes back
		// here rather than to the report page a standard report belongs in. Only set on create: a report's
		// origin does not change when it is edited.
		source: 'AI',
		promptText,
		// Sent on create as well as update: a report whose conditions were tweaked before its first save
		// has already diverged from its prompt by the time the row is created.
		manuallyEdited,
		datePreset: definition.datePreset ?? null,
		dateScope: definition.dateScope ?? null,
		...toSavedReportGroupings(definition),
		...datesOf(definition),
	};
}

/** Builds the update input for re-saving a report that already exists in the Reports list.
 *
 * Three inputs are load-bearing beyond the definition itself:
 * - `reportService: true` — the mutation resolver routes to report-service only when it is set, and
 *   otherwise writes to the legacy saved-report store, where the report does not exist.
 * - `state` — the update overwrites the stored state unconditionally, so omitting it would clear it
 *   and strand the report outside both the saved and temporary listings.
 * - `privateAccess` — likewise overwritten unconditionally, so the report's current value is echoed
 *   back rather than re-asserting the create-time default over a later sharing change. */
export function buildUpdateSavedReportInput(
	saved: SavedReportRef,
	definition: ReportDefinitionV1Tasks,
	manuallyEdited: boolean,
	lockToken?: string | null
): UpdateSavedReportInputFields {
	return {
		id: saved.id,
		// Only when this tab holds a lock. report-service reads a presented token as "this caller
		// participates in the lock protocol" and refuses the write once the token no longer names a live
		// lock it holds, so sending an empty one would turn every unlocked save into a conflict.
		...(lockToken ? {lockToken} : {}),
		name: definition.name,
		eyeApplied: toEyeApplied(definition.fields),
		reportService: true,
		state: ReportState.SAVED,
		privateAccess: saved.privateAccess,
		filters: toSavedReportFilters(definition),
		layout: toSavedReportLayout(definition),
		sort: toSavedReportSort(definition),
		datePreset: definition.datePreset ?? null,
		dateScope: definition.dateScope ?? null,
		// report-service latches this: an update that sends false never lowers a raised flag, so a save
		// from a session that made no manual edit cannot erase one an earlier session made.
		manuallyEdited,
		...toSavedReportGroupings(definition),
		...datesOf(definition),
	};
}
